import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import FadingSpinner from "../../common/FadingSpinner/FadingSpinner";
import {
  approveTimesheetFromEmail,
  getSingleTimesheetFromEmail,
  rejectTimesheetFromEmail
} from "../helpers/fontApi";
import { UpdateStatus } from "../helpers/enum";
import { calculateTimesheetStatus } from "../helpers/helpers";
import { checkIfFeedbackCached } from "../../../helpers/feedback";
import TimesheetFeedback from "../../timesheetFeedback";

const TimesheetReject = ({ tokenStore }) => {
  const { timesheetRef } = useParams();
  const [updateState, setUpdateState] = useState(null);
  const [reason, setReason] = useState("");
  const [rejectedBefore, setRejectedBefore] = useState(false);
  const [approvedBefore, setApprovedBefore] = useState(false);
  const [timesheet, setTimesheet] = useState(null);
  const user = tokenStore.getUser();
  const userEmail = user.emails[0];
  const handleReject = () =>
    rejectTimesheetFromEmail(timesheetRef, setUpdateState, userEmail, reason);

  const handleReload = () => window.location.reload();

  const handleApprove = () =>
    approveTimesheetFromEmail(timesheetRef, setUpdateState, userEmail);

  const styles = {
    container: {
      marginBottom: "10rem"
    },
    containerApproved: {
      marginBottom: checkIfFeedbackCached() ? "28rem" : "50rem"
    },
    containerRejected: {
      marginBottom: "28rem"
    }
  };

  useEffect(() => {
    setUpdateState(UpdateStatus.Loading);
    getSingleTimesheetFromEmail(timesheetRef)
      .then(response => {
        setTimesheet(response);
        const status = calculateTimesheetStatus(response);
        switch (status) {
          case "Pending":
            setUpdateState(UpdateStatus.Hidden);
            break;
          case "Approved":
            setApprovedBefore(true);
            setUpdateState(UpdateStatus.Success);
            break;
          case "Rejected":
            setRejectedBefore(true);
            setUpdateState(UpdateStatus.Rejected);
            break;
          default:
            setUpdateState(UpdateStatus.ErrorReject);
        }
      })
      .catch(error => {
        console.error(error.messages);
        setUpdateState(UpdateStatus.ErrorReject);
      });
  }, []);

  if (updateState === UpdateStatus.Loading) {
    return (
      <div style={styles.container}>
        <div className="timesheet__notification-wrapper">
          <div className="timesheet__notification">
            <FadingSpinner />
            <h3 className="timesheet__notification-title tw-text-2xl tw-font-bold">
              This won't take too long
            </h3>
          </div>
        </div>
      </div>
    );
  }

  if (updateState === UpdateStatus.Hidden) {
    return (
      <div style={styles.container}>
        <div className="mx-auto text-center">
          <h2 className="h2">Reject Timesheet</h2>
          <form>
            <div className="form-group">
              <label htmlFor="rejection-reason">
                Reason for rejecting timesheet:
              </label>
              <textarea
                className="form-control"
                id="rejection-reason"
                value={reason}
                onChange={e => setReason(e.target.value)}
                rows="8"
              ></textarea>
            </div>
            <div className="form-group">
              <button
                className="btn btn-primary btn-lg"
                type="button"
                disabled={reason.length < 3}
                onClick={handleReject}
              >
                Reject
              </button>
            </div>
          </form>
          <div className="form-group tw-mt-7">
            <Link
              to="/timesheets"
              className="btn btn-secondary btn-lg"
              type="button"
            >
              Go to Timesheets
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (updateState === UpdateStatus.Success) {
    if (approvedBefore) {
      return (
        <div style={styles.container}>
          <div className="mx-auto text-center">
            <div className="alert-success" role="alert">
              <strong>Timesheet was approved before!</strong>
            </div>
            <div className="form-group tw-mt-2">
              <Link
                to="/timesheets"
                className="btn btn-primary btn-lg"
                type="button"
              >
                Go to Timesheets
              </Link>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div style={styles.containerApproved}>
        <div className="timesheet__notification-wrapper">
          <div className="timesheet__notification tw-mr-3">
            <img
              src="https://hrgo.spacebetween.co.uk/images/icons/timesheet-approve.svg"
              alt="timesheet-approve"
            />
            <h2 className="timesheet__notification-title text-center tw-font-bold tw-text-3xl tw-pb-2 ">
              Timesheet approved
            </h2>
            <b className="text-center">
              Your approval of the timesheet has been submitted successfully.
            </b>
            <b>Thank you.</b>
            {!checkIfFeedbackCached() && (
              <TimesheetFeedback
                tokenStore={tokenStore}
                candidateRef={timesheet.candidateRef}
                candidateName={timesheet.candidateName}
                timesheetRef={timesheetRef}
              />
            )}
            <div className="form-group tw-mt-10">
              <Link
                to="/timesheets"
                className="btn btn-secondary btn-lg"
                type="button"
              >
                Go to Timesheets
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (updateState === UpdateStatus.Rejected) {
    if (rejectedBefore) {
      return (
        <div style={styles.container}>
          <div className="mx-auto text-center">
            <div className="alert-success" role="alert">
              <strong>Timesheet was rejected before!</strong>
            </div>
            <div className="mx-auto text-center tw-mb-3">
              <b>Do you want to approve the timesheet?</b>
            </div>
            <div className="form-group">
              <button
                className="btn btn-primary btn-lg"
                type="button"
                onClick={handleApprove}
              >
                Approve
              </button>
            </div>
            <div className="form-group tw-mt-10">
              <Link
                to="/timesheets"
                className="btn btn-secondary btn-lg"
                type="button"
              >
                Go to Timesheets
              </Link>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div style={styles.containerRejected}>
        <div className="timesheet__notification-wrapper">
          <div className="timesheet__notification tw-mr-3">
            <img
              src="https://hrgo.spacebetween.co.uk/images/icons/timesheet-reject.svg"
              alt="timesheet-reject"
            />
            <h2 className="timesheet__notification-title text-center tw-text-3xl tw-font-bold tw-pb-4">
              Timesheet rejected
            </h2>
            <b className="text-center">
              Your rejection of the timesheet has been submitted successfully.
            </b>
            <b className="text-center">
              The worker will be informed of the rejection and asked to update
              the timesheet. Thank you.
            </b>
            <div className="form-group tw-mt-10">
              <Link
                to="/timesheets"
                className="btn btn-primary btn-lg"
                type="button"
              >
                Go to Timesheets
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (updateState === UpdateStatus.ErrorReject) {
    return (
      <div style={styles.container}>
        <div className="mx-auto text-center">
          <div className="alert-error" role="alert">
            <strong>Error!</strong>
          </div>
          <div className="form-group">
            <button
              className="btn btn-primary btn-lg"
              type="button"
              onClick={handleReload}
            >
              Try Again
            </button>
          </div>
          <div className="form-group tw-mt-7">
            <Link
              to="/timesheets"
              className="btn btn-secondary btn-lg"
              type="button"
            >
              Go to Timesheets
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return <></>;
};

export default TimesheetReject;
